import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Box, Typography, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 20,
        padding: theme.spacing(2),
        background: '#ffffff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        overflow: 'hidden'
    },
    '& .MuiBackdrop-root': {
        backdropFilter: 'blur(5px)',
        backgroundColor: 'rgba(0, 0, 0, 0.2)'
    }
}));

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        transition: 'all 0.3s ease',
        '&:hover fieldset': {
            borderColor: '#1e88e5'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#1e88e5',
            boxShadow: '0 0 0 4px rgba(30, 136, 229, 0.1)'
        }
    }
});

const GradientButton = styled(Button)({
    background: 'linear-gradient(45deg, #1e88e5 30%, #1565c0 90%)',
    border: 0,
    borderRadius: 12,
    boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    transition: 'transform 0.2s',
    '&:hover': {
        background: 'linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)',
        transform: 'scale(1.02)'
    }
});

// Transition component defined OUTSIDE the main component to prevent re-renders
const Transition = React.forwardRef(function Transition(props, ref) {
    return (
        <motion.div
            ref={ref}
            {...props}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25, stiffness: 300 }}
        >
            {props.children}
        </motion.div>
    );
});

Transition.propTypes = {
    children: PropTypes.node
};

const EditBeneficiaryPopover = ({ open, handleClose, data, onSubmit, loading }) => {
    const [formData, setFormData] = useState({
        benefName: '',
        benefLname: '',
        benefIfsc: '',
        benefAcc: '',
        benefMobile: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (data) {
            setFormData({
                benefName: data.beneficiaryFirstName || '',
                benefLname: data.beneficiaryLastName || '',
                benefIfsc: data.ifscCode || '',
                benefAcc: data.accountNumber || '',
                benefMobile: data.mobileNumber || '',
                ...data
            });
        }
    }, [data]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        let tempErrors = {};
        let isValid = true;

        if (!formData.benefName.trim()) {
            tempErrors.benefName = 'Name is required';
            isValid = false;
        } else if (!/^[a-zA-Z\s]+$/.test(formData.benefName)) {
            tempErrors.benefName = 'First name must contain only characters and spaces';
            isValid = false;
        }

        if (!formData.benefLname?.trim()) {
            // Optional? User said "I need to give the last name also", suggesting it's required for editing.
            // But existing data might not have it? The user said "pass it in a key named lname".
            // Assuming validation rules similar to first name.
            tempErrors.benefLname = 'Last name is required';
            isValid = false;
        } else if (!/^[a-zA-Z\s]+$/.test(formData.benefLname)) {
            tempErrors.benefLname = 'Last name must contain only characters and spaces';
            isValid = false;
        }

        if (!formData.benefIfsc.trim()) {
            tempErrors.benefIfsc = 'IFSC is required';
            isValid = false;
        } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.benefIfsc)) {
            tempErrors.benefIfsc = 'Invalid IFSC format';
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const handleSubmit = () => {
        if (validate()) {
            onSubmit(formData);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm" TransitionComponent={Transition}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <DialogTitle sx={{ p: 0, fontSize: '1.5rem', fontWeight: 700, color: '#333' }}>Edit Beneficiary</DialogTitle>
                        <IconButton onClick={handleClose} size="small" sx={{ color: '#999' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 1, overflowY: 'visible' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                                    First Name
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="benefName"
                                    value={formData.benefName}
                                    onChange={handleChange}
                                    variant="outlined"
                                    placeholder="Enter first name"
                                    error={!!errors.benefName}
                                    helperText={errors.benefName}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                                    Last Name
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="benefLname"
                                    value={formData.benefLname}
                                    onChange={handleChange}
                                    variant="outlined"
                                    placeholder="Enter last name"
                                    error={!!errors.benefLname}
                                    helperText={errors.benefLname}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                                    IFSC Code
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="benefIfsc"
                                    value={formData.benefIfsc}
                                    onChange={handleChange}
                                    variant="outlined"
                                    placeholder="Enter IFSC code"
                                    error={!!errors.benefIfsc}
                                    helperText={errors.benefIfsc}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                                    Account Number
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    value={formData.benefAcc}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                        style: { backgroundColor: '#f5f5f7', color: '#888' }
                                    }}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                                    Mobile Number
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    value={formData.benefMobile}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                        style: { backgroundColor: '#f5f5f7', color: '#888' }
                                    }}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 1, pt: 3 }}>
                        <Button
                            onClick={handleClose}
                            sx={{
                                color: '#666',
                                borderRadius: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3
                            }}
                        >
                            Cancel
                        </Button>
                        <GradientButton onClick={handleSubmit} disabled={loading} disableElevation>
                            {loading ? 'Updating...' : 'Save Changes'}
                        </GradientButton>
                    </DialogActions>
                </StyledDialog>
            )}
        </AnimatePresence>
    );
};

EditBeneficiaryPopover.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    data: PropTypes.shape({
        benefName: PropTypes.string,
        benefLname: PropTypes.string,
        benefIfsc: PropTypes.string,
        benefAcc: PropTypes.string,
        benefMobile: PropTypes.string,
        beneficiaryFirstName: PropTypes.string,
        beneficiaryLastName: PropTypes.string,
        ifscCode: PropTypes.string,
        accountNumber: PropTypes.string,
        mobileNumber: PropTypes.string
    }),
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired
};

export default EditBeneficiaryPopover;
